import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Snackbar,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import api from '../../api';
import { EffectiveTenantService } from '../../api/serviceCatalog';
import RegularInput from '../input/RegularInput';
import SettingsCard from './SettingsCard';

/**
 * Calculator v3 — tenant-editable service catalog.
 *
 * Lists every service available in this tenant's Workloading dropdown
 * (CONTRACT classification only), shows the effective rate, lets the
 * operator override the rate inline, add a tenant-only service, or
 * remove a tenant override / addition.
 *
 * Reference rows from the global catalog show "default X sqft/hr" as a
 * hint when the tenant's effective rate differs from the default. PROJECT
 * and AD_HOC services don't appear here — they belong to other flows.
 *
 * Hides silently for non-Calc-v3 tenants (the API 403s).
 */
const ServiceCatalogCardDefaults = {};
const ServiceCatalogCard: React.FC = function ServiceCatalogCard() {
  const [services, setServices] = useState<EffectiveTenantService[]>([]);
  const [hidden, setHidden] = useState<boolean>(false);
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  const [snackMsg, setSnackMsg] = useState<string>('');

  // Inline-edit state for the rate column. Keyed by code so we don't have
  // to track which row is "active" — every row has its own draft cell.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // New-addition row.
  const [newName, setNewName] = useState<string>('');
  const [newRate, setNewRate] = useState<string>('');

  const load = async () => {
    try {
      const list = await api.ServiceCatalogApi.list('CONTRACT');
      setServices(list);
      setDrafts({});
      setHidden(false);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHidden(true);
      } else {
        setSnackMsg(
          err?.response?.data?.message ?? err?.message ?? 'Load failed.'
        );
        setSnackOpen(true);
      }
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveOverride = async (svc: EffectiveTenantService) => {
    if (!svc.referenceServiceId) return;
    const draft = drafts[svc.code];
    const rateNum = draft === '' || draft == null ? null : Number(draft);
    if (rateNum != null && (Number.isNaN(rateNum) || rateNum < 0)) {
      setSnackMsg('Rate must be a positive number.');
      setSnackOpen(true);
      return;
    }
    try {
      const next = await api.ServiceCatalogApi.upsertOverride({
        rateSqftPerHour: rateNum,
        referenceServiceId: svc.referenceServiceId
      });
      setServices(next);
      setDrafts((d) => {
        const copy = { ...d };
        delete copy[svc.code];
        return copy;
      });
      setSnackMsg(`${svc.name} updated.`);
      setSnackOpen(true);
    } catch (err: any) {
      setSnackMsg(
        err?.response?.data?.message ?? err?.message ?? 'Save failed.'
      );
      setSnackOpen(true);
    }
  };

  const resetToDefault = async (svc: EffectiveTenantService) => {
    if (!svc.tenantRowId) return;
    try {
      const next = await api.ServiceCatalogApi.deleteTenantRow(svc.tenantRowId);
      setServices(next);
      setSnackMsg(`${svc.name} reset to default.`);
      setSnackOpen(true);
    } catch (err: any) {
      setSnackMsg(
        err?.response?.data?.message ?? err?.message ?? 'Reset failed.'
      );
      setSnackOpen(true);
    }
  };

  const addCustom = async () => {
    const rateNum = Number(newRate);
    if (!newName.trim() || !(rateNum > 0)) {
      setSnackMsg('Provide a name and a positive rate.');
      setSnackOpen(true);
      return;
    }
    try {
      const next = await api.ServiceCatalogApi.createAddition({
        name: newName.trim(),
        rateSqftPerHour: rateNum
      });
      setServices(next);
      setNewName('');
      setNewRate('');
      setSnackMsg('Service type added.');
      setSnackOpen(true);
    } catch (err: any) {
      setSnackMsg(
        err?.response?.data?.message ?? err?.message ?? 'Add failed.'
      );
      setSnackOpen(true);
    }
  };

  if (hidden) return null;

  return (
    <SettingsCard title="Workloading Service Catalog">
      <Typography color="secondary.500" sx={{ mb: 2 }} variant="body_200">
        These are the contract service types your team can pick when running
        Workloading. Tweak the sqft/hour rate to match your historical
        throughput, or add a custom type for verticals not in the default
        catalog.
      </Typography>

      {/* Header row */}
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '2fr 1fr 1fr auto',
          pb: 1
        }}
      >
        <Typography color="mainText.500" variant="primary-bold">
          Service
        </Typography>
        <Typography color="mainText.500" variant="primary-bold">
          Rate (sqft/hr)
        </Typography>
        <Typography color="mainText.500" variant="primary-bold">
          Default
        </Typography>
        <Box />
      </Box>

      {services.length === 0 && (
        <Box
          sx={{
            bgcolor: '#FFF4DC',
            border: '1px solid #F5B062',
            borderRadius: 1,
            mt: 2,
            p: 2
          }}
        >
          <Typography color="#7A4A00" variant="body_200">
            <strong>Catalog is empty.</strong> The service-catalog tables
            haven&apos;t been seeded for this environment yet — ask your
            DBA / Bluberry support to run migration{' '}
            <code>1777000000007-ServiceCatalog.sql</code>. You can still
            add tenant-custom services below, but the 8 global default
            types (Nightly Office, Medical, Day Porter, etc.) won&apos;t
            show until the seed runs.
          </Typography>
        </Box>
      )}

      {services.map((svc) => {
        const draftValue =
          drafts[svc.code] != null
            ? drafts[svc.code]
            : String(svc.baseRateSqftPerHour);
        const isOverride =
          svc.hasTenantRow && svc.referenceServiceId != null;
        const isAddition =
          svc.hasTenantRow && svc.referenceServiceId == null;
        return (
          <Box
            key={svc.code}
            sx={{
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'grid',
              gap: 1,
              gridTemplateColumns: '2fr 1fr 1fr auto',
              py: 1.25
            }}
          >
            <Box>
              <Typography variant="body_300">{svc.name}</Typography>
              <Typography color="secondary.400" variant="body_100">
                {(() => {
                  if (isAddition) return 'Custom (tenant-only)';
                  if (isOverride) return 'Override of catalog default';
                  return 'Catalog default';
                })()}
              </Typography>
            </Box>
            <RegularInput
              onChange={(e: any) =>
                setDrafts((d) => ({ ...d, [svc.code]: e.target.value }))
              }
              type="number"
              value={draftValue}
            />
            <Typography color="secondary.400" variant="body_200">
              {svc.referenceDefaultRate != null
                ? `${svc.referenceDefaultRate}`
                : '—'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {svc.referenceServiceId && (
                <Tooltip
                  arrow
                  title={
                    drafts[svc.code] != null
                      ? 'Save tenant override'
                      : 'No changes'
                  }
                >
                  <span>
                    <Button
                      disabled={drafts[svc.code] == null}
                      onClick={() => saveOverride(svc)}
                      size="small"
                      variant="outlined"
                    >
                      Save
                    </Button>
                  </span>
                </Tooltip>
              )}
              {isOverride && (
                <Tooltip arrow title="Reset to catalog default">
                  <IconButton onClick={() => resetToDefault(svc)} size="small">
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {isAddition && (
                <Tooltip arrow title="Remove this custom type">
                  <IconButton onClick={() => resetToDefault(svc)} size="small">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      })}

      {/* Add row */}
      <Box
        sx={{
          alignItems: 'center',
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '2fr 1fr 1fr auto',
          mt: 2
        }}
      >
        <RegularInput
          onChange={(e: any) => setNewName(e.target.value)}
          placeholder="Add custom service (e.g. Gym & Fitness)"
          type="text"
          value={newName}
        />
        <RegularInput
          onChange={(e: any) => setNewRate(e.target.value)}
          placeholder="sqft/hr"
          type="number"
          value={newRate}
        />
        <Box />
        <Tooltip arrow title="Add this custom service to your catalog">
          <span>
            <Button
              disabled={!newName.trim() || !(Number(newRate) > 0)}
              onClick={addCustom}
              size="small"
              startIcon={<AddIcon />}
              variant="contained"
            >
              Add
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Snackbar
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        autoHideDuration={3000}
        message={snackMsg}
        onClose={() => setSnackOpen(false)}
        open={snackOpen}
      />
    </SettingsCard>
  );
};
ServiceCatalogCard.defaultProps = ServiceCatalogCardDefaults;

export default ServiceCatalogCard;
